import { useMemo } from "react";
import { useUrlSearch } from "@/shared/hooks/useUrlSearch";
import { useWhatsappCampaignsQuery } from "../api";
import { TableActions, tableColumns } from "../components";
import type { ColumnDef } from "@tanstack/react-table";
import type {
  CampaignsFilterValues,
  CampaignsQueryParams,
  WhatsappCampaign,
} from "../types";
import { defaultCampaignsFilterValues } from "../types";

export const useWhatsappCampaignPage = () => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<CampaignsFilterValues>({
    defaultValues: defaultCampaignsFilterValues,
  });

  // Query with URL-based params
  const queryParams = useMemo<CampaignsQueryParams>(
    () => ({ page, perSize: size, search: appliedValues }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useWhatsappCampaignsQuery(queryParams);

  const data = queryData?.data || [];
  const totalItems = queryData?.totalItems || 0;

  // Columns with actions
  const columns = useMemo<ColumnDef<WhatsappCampaign>[]>(
    () => [
      ...tableColumns,
      {
        id: "actions",
        header: "الخيارات",
        meta: { grow: 1 },
        cell: ({ row }) => <TableActions row={row.original} />,
      },
    ],
    [],
  );

  return {
    // Data
    data,
    columns,
    isLoading,
    error,
    totalItems,
    // Search (for compatibility with existing components)
    search: { show: showFilters, values: draftValues },
    updateSearch: (updates: {
      show?: boolean;
      values?: Partial<CampaignsFilterValues>;
    }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof CampaignsFilterValues, value);
        });
      }
    },
    applySearch,
    resetSearch,
    // Pagination (for compatibility with existing components)
    pg: { page, size },
    updatePg: (updates: { page?: number; size?: number }) => {
      if (updates.page !== undefined) setPage(updates.page);
      if (updates.size !== undefined) setSize(updates.size);
    },
    // Actions
    refetch,
  };
};
